import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";
import { FacebookTracking } from "@/utils/facebookTracking";
import {
  registerFormSchema,
  RegisterFormValues,
} from "@/components/auth/RegisterFormSchema";

// Registration process steps
enum RegistrationStep {
  INITIAL = "initial",
  CREATING_ACCOUNT = "creating_account",
  WAITING_FOR_SESSION = "waiting_for_session",
  CREATING_ORGANIZATION = "creating_organization",
  FINALIZING = "finalizing",
  WAITING_FOR_PROFILE_REFRESH = "waiting_for_profile_refresh",
  WAITING_FOR_AUTH_CONTEXT = "waiting_for_auth_context",
  PREPARING_ONBOARDING = "preparing_onboarding",
  TAKING_A_BREATH = "taking_a_breath",
  COMPLETE = "complete",
  ERROR = "error",
}

export function useRegisterForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [currentStep, setCurrentStep] = useState<RegistrationStep>(
    RegistrationStep.INITIAL,
  );
  const [error, setError] = useState<string | null>(null);
  const { signUp, refreshProfile, organization } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Generate loading messages based on current step
  const loadingMessages = () => {
    switch (currentStep) {
      case RegistrationStep.CREATING_ACCOUNT:
        return ["Welcome to Hubflo Agents ☺️"];
      case RegistrationStep.WAITING_FOR_SESSION:
        return ["Welcome to Hubflo Agents ☺️"];
      case RegistrationStep.CREATING_ORGANIZATION:
        return ["Ready to hire your first AI employees?"];
      case RegistrationStep.FINALIZING:
        return ["Ready to hire your first AI employees?"];
      case RegistrationStep.WAITING_FOR_PROFILE_REFRESH:
        return ["Ready to hire your first AI employees?"];
      case RegistrationStep.WAITING_FOR_AUTH_CONTEXT:
        return ["Setting up your AI workspace..."];
      case RegistrationStep.PREPARING_ONBOARDING:
        return ["Setting up your AI workspace..."];
      case RegistrationStep.TAKING_A_BREATH:
        return ["Let's go!"];
      case RegistrationStep.ERROR:
        return ["Error encountered", error || "Something went wrong"];
      default:
        return ["Let's go!"];
    }
  };

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerFormSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // Removed broken AuthContext waiting logic - registration should complete successfully
  // and let AuthContext/DefaultRouteHandler handle data loading and redirects

  const onSubmit = async (data: RegisterFormValues) => {
    try {
      // Mark registration as in progress to prevent AuthContext interference
      sessionStorage.setItem("registrationInProgress", "true");

      // Capture fbclid from Facebook tracking before signup
      const fbclid = FacebookTracking.getFbclid();

      // Start loading state and show full screen loader
      setIsLoading(true);
      setShowLoadingScreen(true);
      setCurrentStep(RegistrationStep.CREATING_ACCOUNT);

      // Step 1: Sign up the user with fbclid in metadata
      const { error: signUpError } = await signUp(
        data.email,
        data.password,
        undefined, // organizationName not used anymore
        fbclid, // Pass fbclid to signup
      );

      if (signUpError) {
        console.error("Registration error:", signUpError);
        setCurrentStep(RegistrationStep.ERROR);
        setError(signUpError.message);
        throw signUpError;
      }

      // Update step to waiting for session
      setCurrentStep(RegistrationStep.WAITING_FOR_SESSION);

      // Step 2: Wait for the auth session to be established
      // IMPORTANT: This requires email confirmation to be DISABLED in Supabase
      // If email confirmation is enabled, signUp will return user but session will be null
      // and this wait will timeout. The user would need to confirm their email first.
      let retries = 0;
      const maxRetries = 15;
      const retryInterval = 500; // ms

      const waitForSession = async (): Promise<{ session: Session }> => {
        const { data: session } = await supabase.auth.getSession();

        if (!session.session?.user) {
          if (retries >= maxRetries) {
            throw new Error(
              "Authentication session not found after multiple attempts",
            );
          }

          retries++;

          // Wait and try again
          return new Promise((resolve) => {
            setTimeout(async () => {
              const result = await waitForSession();
              resolve(result);
            }, retryInterval);
          });
        }

        return session;
      };

      // Wait for session to be established
      const session = await waitForSession();

      // First check if user already has an organization (e.g., from OAuth triggers)
      const { data: existingProfile } = await supabase
        .from("profiles")
        .select("organization_id")
        .eq("id", session.session.user.id)
        .single();

      if (existingProfile?.organization_id) {
        // Proceed to onboarding
      }

      // Step 4: Registration complete - let AuthContext handle data loading in background
      setCurrentStep(RegistrationStep.PREPARING_ONBOARDING);

      // Trigger profile refresh but don't wait - let AuthContext and DefaultRouteHandler handle it
      refreshProfile();

      // Brief pause to show success state
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Step 6: Backup fbclid capture (in case trigger didn't work)
      if (organization?.id && fbclid) {
        try {
          const { updateOrganizationWithFbclid } = await import(
            "@/services/organizations"
          );
          const fbclidResult = await updateOrganizationWithFbclid(
            organization.id,
          );
          if (fbclidResult.success && fbclidResult.fbclid) {
          } else if (!fbclidResult.success) {
            console.warn(
              "[Registration] Backup fbclid capture failed:",
              fbclidResult.error,
            );
          }
        } catch (error) {
          console.warn(
            "[Registration] Error during backup fbclid capture:",
            error,
          );
          // Don't fail registration for fbclid issues
        }
      }

      // Step 7: Prepare for onboarding
      setCurrentStep(RegistrationStep.PREPARING_ONBOARDING);

      // Step 8: Mark as complete
      setCurrentStep(RegistrationStep.COMPLETE);

      // Success notification removed - user will see onboarding flow instead

      // Brief pause to show the success state
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Clear the registration in progress flag
      sessionStorage.removeItem("registrationInProgress");

      // Set more persistent flags for new user onboarding
      sessionStorage.setItem("newUserOnboarding", "true");
      sessionStorage.setItem("pendingNewUserRedirect", "true");

      // Navigate directly to onboarding to avoid loading data contexts for new users
      navigate("/user-onboarding", {
        replace: true,
        state: {
          isNewUser: true,
          fromAuth: true,
          registrationCompleted: true,
          timestamp: Date.now(), // Add timestamp for debugging
        },
      });
    } catch (error: unknown) {
      console.error("Registration error:", error);
      setCurrentStep(RegistrationStep.ERROR);
      setError(
        error instanceof Error
          ? error.message
          : "An unexpected error occurred during registration",
      );

      toast({
        variant: "destructive",
        title: "Registration Failed",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred during registration.",
      });
    } finally {
      // Always reset loading states and clear the registration flag regardless of success or error
      sessionStorage.removeItem("registrationInProgress");
      setIsLoading(false);
      setShowLoadingScreen(false);
    }
  };

  return {
    form,
    isLoading,
    showLoadingScreen,
    loadingMessages: loadingMessages(),
    currentStep,
    onSubmit: form.handleSubmit(onSubmit),
  };
}
